<template>
  <article class="image-selector-wrapper">
    <div
      v-if="props.carouselImages.length > IMAGE_PER_VIEW && orientation === ALT_IMAGE_ORIENTATION.vertical"
      class="chevron-up-wrap"
      :class="{ invisible: altImagePage === 1 }"
      @click="prev"
    >
      <div
        class="chevron-medium-up-black"
        role="button"
        aria-label="scroll up images rail"
      />
    </div>
    <div class="alt-image-container">
      <ul
        v-if="orientation === ALT_IMAGE_ORIENTATION.vertical && isMounted"
        class="alt-desktop-images"
        :style="{ transform: 'translate(' + '0px, ' + initValue + 'px)' }"
      >
        <li
          v-for="(src, i) in props.carouselImages"
          :key="i"
          v-bind="ariaData[i]"
          class="image-wrapper"
          :class="[{ active: currentPage - 1 === i }, { hovered: currentPageOnHover === i }, { 'video-wrapper': src.video }]"
          @click="selectImage(i)"
          @mouseover="enterMouse(i)"
          @mouseleave="leaveMouse"
        >
          <AltImageItem
            :index="i"
            :data="src"
            :video-id="videoId"
            :images="props.carouselImages"
            :current-page="currentPage"
            :seo-alt="props.seoAlt"
          />
        </li>
      </ul>
      <div
        v-if="orientation === ALT_IMAGE_ORIENTATION.horizontal"
        class="carousel-image-for-tablet margin-top-xs"
        :class="{
          last: isLastPage,
          full: props.carouselImages.length <= IMAGE_PER_VIEW_FOR_TABLET,
        }"
      >
        <Carousel
          v-if="isMounted"
          ref="altCarousel"
          :list="props.carouselImages"
          :number-of-items="{ large: 4, medium: 4, small: 4 }"
          :gutter="{ large: 8, medium: 8, small: 8 }"
          @carousel-next="nextClick"
          @carousel-prev="prevClick"
        >
          <template #item="{ data, index }">
            <div
              :key="index"
              class="image-wrapper horizontal"
              :class="[{ active: currentPage - 1 === index }, { hovered: currentPageOnHover === index }, { 'video-wrapper': data.video }]"
              @click="selectImage(index)"
              @mouseover="enterMouse(index)"
              @mouseleave="leaveMouse"
            >
              <AltImageItem
                :index="index"
                :data="data"
                :video-id="videoId"
                :images="props.carouselImages"
                :current-page="currentPage"
                :seo-alt="props.seoAlt"
              />
            </div>
          </template>
        </Carousel>
      </div>
    </div>
    <div
      v-if="props.carouselImages.length > IMAGE_PER_VIEW && orientation === ALT_IMAGE_ORIENTATION.vertical"
      class="chevron-down-wrap"
      :class="{
        invisible: hideNextArrow || props.carouselImages.length === pageRanges.last,
      }"
      @click="next"
    >
      <div
        class="chevron-medium-down-black"
        role="button"
        aria-label="scroll down images rail"
      />
    </div>
  </article>
</template>
<script setup>
import { useMounted } from '@vueuse/core';
import { ref, watch, computed } from 'vue';
import Carousel from '@atomic-ui/carousel';
import once from '@utils/once';
import { useDeviceType } from '@composables/utils/useDeviceType';
import { useBrand } from '@composables/useBrand';
import AltImageItem from './AltImageItem';
import { ALT_IMAGE_ORIENTATION } from '../../../constants/common';
import { useImages } from '../../../composables/useImages';
import { tagAltImagesClicked, tagImageClicked } from '../../../features/analytics/image';

const ALT_IMAGE_SIZE_FOR_DESKTOP_LARGE = 110;
const ALT_IMAGE_SIZE_FOR_DESKTOP_MEDIUM = 63;
const IMAGE_PER_VIEW = 5;
const IMAGE_PER_VIEW_FOR_TABLET = 4;

const { videoId } = useImages();
const isMounted = useMounted();
const { isMcom } = useBrand();

const emits = defineEmits(['imageChange', 'imageChangeOnHover']);
const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  currentPageOnHover: {
    /** @type {import('vue').PropType<number | null>} */
    type: Number || null,
    default: null,
  },
  carouselImages: {
    /** @type {import('vue').PropType<Image[]>} */
    type: Array,
    required: true,
  },
  orientation: {
    type: String,
    default: ALT_IMAGE_ORIENTATION.vertical,
    validator(orientation) {
      return Object.values(ALT_IMAGE_ORIENTATION).includes(orientation);
    },
  },
  seoAlt: {
    type: String,
    default: '',
  },
});

const { isTablet, isDesktopMedium } = useDeviceType();
const fireAnalytics = once(tagAltImagesClicked);

const initValue = ref(0);
const timeOut = ref(0);
const isLastPage = ref(false);
const hideNextArrow = ref(false);
const hidePrevArrow = ref(true);
const altImagePage = ref(1);
const pageRanges = ref({
  first: 1,
  last: IMAGE_PER_VIEW,
});
const tabletPageRanges = ref({
  first: 1,
  last: IMAGE_PER_VIEW_FOR_TABLET,
});
const altCarousel = ref(null);
const ariaData = computed(() => props.carouselImages.map((mediaItem, i) => ({
  'aria-label': `Display ${props.seoAlt} ${mediaItem.video ? 'Video' : 'Image'}`,
  'aria-posinset': i + 1,
  'aria-setsize': props.carouselImages.length,
})));
/**
 * @param {number} index
 */
const selectImage = (index) => {
  emits('imageChange', index + 1);
  return isMcom ? fireAnalytics() : tagImageClicked(index + 1);
};
const canMoveByUnit = computed(
  () => (props.carouselImages.length - altImagePage.value * IMAGE_PER_VIEW) / IMAGE_PER_VIEW,
);
const altImageSize = computed(() => {
  if (
    (props.orientation === ALT_IMAGE_ORIENTATION.vertical && isTablet.value) || (isDesktopMedium.value)
  ) {
    return ALT_IMAGE_SIZE_FOR_DESKTOP_MEDIUM;
  }
  return ALT_IMAGE_SIZE_FOR_DESKTOP_LARGE;
});
const resetFields = () => {
  initValue.value = 0;
  altImagePage.value = 1;
  hideNextArrow.value = false;
  pageRanges.value = {
    first: 1,
    last: IMAGE_PER_VIEW,
  };
  tabletPageRanges.value = {
    first: 1,
    last: IMAGE_PER_VIEW_FOR_TABLET,
  };
};

const prev = () => {
  if (altImagePage.value === 1) return;
  hideNextArrow.value = false;
  if (altImagePage.value === 2) {
    resetFields();
    return;
  }

  initValue.value += altImageSize.value * IMAGE_PER_VIEW;

  if (altImagePage.value > 1) {
    altImagePage.value -= 1;
  }
  if (
    props.carouselImages.length % IMAGE_PER_VIEW > 0
      && altImagePage.value + 1 === Math.ceil(props.carouselImages.length / IMAGE_PER_VIEW)
  ) {
    pageRanges.value = {
      first: pageRanges.value.first - IMAGE_PER_VIEW,
      last: pageRanges.value.last - (props.carouselImages.length % IMAGE_PER_VIEW),
    };
    return;
  }

  pageRanges.value = {
    first: pageRanges.value.first - IMAGE_PER_VIEW,
    last: pageRanges.value.last - IMAGE_PER_VIEW,
  };
};
const next = () => {
  if (hideNextArrow.value || props.carouselImages.length === pageRanges.value.last) return;
  if (props.carouselImages.length > IMAGE_PER_VIEW && canMoveByUnit.value >= 0) {
    initValue.value -= altImageSize.value * IMAGE_PER_VIEW;
  } else if (props.carouselImages.length > IMAGE_PER_VIEW && canMoveByUnit.value < 0) {
    initValue.value
        -= altImageSize.value
        * ((props.carouselImages.length - (altImagePage.value - 1) * IMAGE_PER_VIEW) % IMAGE_PER_VIEW);
    hideNextArrow.value = true;
  }
  pageRanges.value = {
    first: pageRanges.value.last + 1,
    last: (pageRanges.value.last + IMAGE_PER_VIEW) > props.carouselImages.length
      ? props.carouselImages.length
      : pageRanges.value.last + IMAGE_PER_VIEW,
  };
  if (
    props.carouselImages.length % IMAGE_PER_VIEW > 0
      && altImagePage.value === Math.ceil(props.carouselImages.length / IMAGE_PER_VIEW)
  ) {
    pageRanges.value = {
      first: props.carouselImages.length - 4,
      last: props.carouselImages.length,
    };
  }
  altImagePage.value += 1;
  hidePrevArrow.value = false;
};
/**
 * @param {number} index
 */
const enterMouse = (index) => {
  clearTimeout(timeOut.value);
  emits('imageChangeOnHover', index);
};
const leaveMouse = () => {
  timeOut.value = setTimeout(() => {
    emits('imageChangeOnHover', null);
  }, 10);
};
const nextClick = (ev, updateCarousel = false) => {
  if (updateCarousel && altCarousel.value) {
    altCarousel.value.next();
    return;
  }
  isLastPage.value = ev === Math.ceil(props.carouselImages.length / IMAGE_PER_VIEW_FOR_TABLET);
  if (isLastPage.value) {
    tabletPageRanges.value = {
      first: props.carouselImages.length - 3,
      last: props.carouselImages.length,
    };
    return;
  }
  tabletPageRanges.value = {
    first: tabletPageRanges.value.last + 1,
    last: tabletPageRanges.value.last + IMAGE_PER_VIEW_FOR_TABLET,
  };
};
const prevClick = (ev, updateCarousel = false) => {
  if (updateCarousel && altCarousel.value) {
    altCarousel.value.prev();
    return;
  }
  isLastPage.value = false;

  if (ev === 1) {
    tabletPageRanges.value = {
      first: 1,
      last: IMAGE_PER_VIEW_FOR_TABLET,
    };
    return;
  }
  const pageCount = Math.ceil(props.carouselImages.length / IMAGE_PER_VIEW_FOR_TABLET);
  const lefOverItems = props.carouselImages.length % IMAGE_PER_VIEW_FOR_TABLET;
  if (lefOverItems > 0 && pageCount - 1 === ev) {
    tabletPageRanges.value = {
      first: tabletPageRanges.value.first - lefOverItems,
      last: props.carouselImages.length - lefOverItems,
    };
  } else {
    tabletPageRanges.value = {
      first: tabletPageRanges.value.first - IMAGE_PER_VIEW_FOR_TABLET,
      last: tabletPageRanges.value.first - 1,
    };
  }
};

const controlTabletNavigation = (value) => {
  if (props.carouselImages.length <= IMAGE_PER_VIEW_FOR_TABLET) return;
  if (value === 1) {
    altCarousel.value.currentPage = value;
    isLastPage.value = false;
    altCarousel.value.redraw();
    resetFields();
  } else if (value === props.carouselImages.length) {
    altCarousel.value.currentPage = Math.ceil(props.carouselImages.length / IMAGE_PER_VIEW_FOR_TABLET);
    altCarousel.value.redraw();
    tabletPageRanges.value = {
      first: props.carouselImages.length - 3,
      last: props.carouselImages.length,
    };
    isLastPage.value = true;
  } else if (value > tabletPageRanges.value.last) {
    nextClick(value, true);
  } else if (value < tabletPageRanges.value.first) {
    prevClick(value, true);
  }
};

const controlDesktopNavigation = (value) => {
  if (props.carouselImages.length <= IMAGE_PER_VIEW) return;
  if (value === 1) {
    // move alt images to start point
    resetFields();
  } else if (value === props.carouselImages.length && Math.ceil(props.carouselImages.length / IMAGE_PER_VIEW) > 1) {
    // move alt images to last point
    if (pageRanges.value.last === props.carouselImages.length) return;
    if (canMoveByUnit.value < 0) {
      initValue.value = -(
        altImageSize.value
          * (IMAGE_PER_VIEW * (altImagePage.value - 1) + (props.carouselImages.length % IMAGE_PER_VIEW) - IMAGE_PER_VIEW)
      );
      pageRanges.value = {
        first: pageRanges.value.last + 1,
        last: (pageRanges.value.last + IMAGE_PER_VIEW) > props.carouselImages.length
          ? props.carouselImages.length
          : pageRanges.value.last + IMAGE_PER_VIEW,
      };
    } else {
      const pages = Math.ceil(props.carouselImages.length / IMAGE_PER_VIEW);
      pageRanges.value = {
        first: ((pages - 1) * IMAGE_PER_VIEW) + 1,
        last: props.carouselImages.length,
      };
      initValue.value = -((pages - 1) * IMAGE_PER_VIEW) * altImageSize.value;
    }
    altImagePage.value = Math.ceil(props.carouselImages.length / IMAGE_PER_VIEW);
    hideNextArrow.value = true;
  } else if (value > pageRanges.value.last) {
    next();
  } else if (value < pageRanges.value.first) {
    prev();
  }
};

watch(
  () => props.currentPage,
  (value) => {
    if (isTablet.value && altCarousel.value && props.orientation === ALT_IMAGE_ORIENTATION.horizontal) {
      controlTabletNavigation(value);
    } else {
      controlDesktopNavigation(value);
    }
  },
);

watch(
  () => props.carouselImages.length,
  () => {
    resetFields();
  },
);
</script>
<style lang="scss" scoped>
  @import "@csgIcons/_play-control.scss";

  @import "~settingSCSS";
  .alt-image-container {
    max-height: rem-calc(542);
    overflow: hidden;
    ul {
      transition: 0.2s ease;
      overflow: hidden;
    }
    @include breakpoint(large down) {
      max-height: rem-calc(307);
    }
  }
  .image-wrapper {
    position: relative;
    margin-bottom: rem-calc(8);
    transition: 0.2s ease;
    opacity: 0.8;
    display: flex;
    align-items: center;
    width: fit-content;
    &.video-wrapper {
    opacity: 1;
      :deep() {
        .picture-container {
          opacity: 0.5;
          transition: .2s ease;
        }
      }
    }
    &.hovered,
    &.active {
      opacity: 1;
      :deep() {

        .video {
          .picture-container {
              opacity: 1;
            }
        }
      }
    }
    &.horizontal {
      margin-bottom: 0;
      :deep {
        .alt-img {
          @include breakpoint(medium down) {
            width: rem-calc(60);
            height: rem-calc(75);
            max-height: rem-calc(75);
          }
        }
      }
    }
  }
  .image-selector-wrapper {
    height: 100%;
  }
  .chevron-down-wrap {
    margin-top: rem-calc(8);
  }
  .chevron-up-wrap {
    margin-bottom: rem-calc(8);
  }
  .chevron-up-wrap,
  .chevron-down-wrap {
    width: rem-calc(82);
    display: flex;
    justify-content: center;
    cursor: pointer;
    &.disabled {
      opacity: 0.35;
      cursor: default;
    }
    .chevron-medium-up-black,
    .chevron-medium-down-black {
      margin: 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      @include breakpoint(large down) {
        margin: unset;
      }
    }
    @include breakpoint(large down) {
      width: rem-calc(44);
    }

    @include breakpoint(large) {
      display: flex;
    }
  }
  .chevron-medium-down-black,
  .chevron-medium-up-black {
    width: rem-calc(32);
    height: rem-calc(32);
    background-size: rem-calc(32) rem-calc(32);
  }
  .carousel-image-for-tablet {
    display: none;
    @include breakpoint(medium only) {
      display: block;
    }
    :deep() {
      .v-carousel {
        margin-left: 46px !important;
        margin-right: 46px !important;
      }
      .v-carousel-wrapper {
        position: unset;
      }
      .v-carousel-content {
        overflow: visible;
      }
      .v-button-wrapper.prev {
        left: -40px;
      }
      .v-button-wrapper.next {
        right: -40px;
      }
      .v-carousel-button {
        display: flex;
        background-color: transparent;
        &[tabindex="-1"] {
          display: none;
        }
      }
      .v-button-wrapper {
        display: flex !important;
        transform: translateX(0);
      }
    }
    &.last {
      :deep() {
        .v-carousel {
          margin-right: 92px !important;
        }
        .v-carousel-wrapper {
          margin-right: -46px !important;
        }
        .v-button-wrapper.next {
          right: -90px;
        }
        .v-scroller {
          margin-left: -20px !important;
        }
      }
    }
    &.full {
      :deep() {
        .v-carousel {
          margin-left: 0px !important;
          margin-right: 92px !important;
        }
        .v-button-wrapper {
          display: none !important;
        }
      }
    }
  }
</style>
